import _ from 'lodash'
import { z } from 'zod'
import { RImportResultLocation } from './ImportResultLocation'
import { err, ok, Result } from 'neverthrow'

export enum ConceptStatusValueType {
	Chosen = 'chosen',
	Derived = 'derived',
	Exported = 'exported', // loaded in from database
}
export const RConceptStatusValueType = z.enum(ConceptStatusValueType)

// this is the historic "ConceptStatusValue" type;
// complex values were always stringified before submitStatusUpdate,
// but now import/export in the api handles native objects
// (ConceptValueDataType.Object)
// so we can get away from all the stringify stuff over time
export const RConceptStatusValuePrimitive = z.union([
	z.string(),
	z.number(),
	z.boolean(),
	z.null(),
])
export type ConceptStatusValuePrimitive = z.infer<typeof RConceptStatusValuePrimitive>
// there is no good "json" type in typescript, and redux + the ts typechecker
// isn't handling recursive types well.  after some experimentation
// (ConceptStatus.test.ts) this seemed like a reasonable middle ground
// because inbound models can be safeParsed with zod anyway before use
export const RConceptStatusValueComplex = z.union([
	z.array(z.unknown()),
	z.record(z.string(), z.unknown()),
])
export type ConceptStatusValueComplex = z.infer<typeof RConceptStatusValueComplex>
export const RConceptStatusValue = z.union([
	RConceptStatusValuePrimitive,
	RConceptStatusValueComplex,
])
export type ConceptStatusValue = z.infer<typeof RConceptStatusValue>
// ensure a value is primitive
export const primitiveValue = (value: ConceptStatusValue): Result<ConceptStatusValuePrimitive, void> => {
	// check for null first, because typeof null === 'object' 🤡
	if(value === null || typeof value !== 'object') {
		return ok(value)
	}
	return err()
}

export const RConceptStatus = z.object({
	cid: z.string(),
	value: RConceptStatusValue,
	valueType: RConceptStatusValueType,
	updatedAt: z.string(),
	userId: z.string(),
	// optional if a result hasn't changed since it was exported from the
	// server, which does not provide us the persisted location
	location: z.optional(RImportResultLocation),
})
export type ConceptStatus = z.infer<typeof RConceptStatus>

export const RSuperconceptStatus = z.object({
	cid: z.string(),
	subconcepts: z.record(z.string(), RConceptStatus),
	updatedAt: z.optional(z.string()),
	userId: z.optional(z.string()),
	// optional if a result hasn't changed since it was exported from the
	// server, which does not provide us the persisted location
	location: z.optional(RImportResultLocation),
})
export type SuperconceptStatus = z.infer<typeof RSuperconceptStatus>

export const RAnyConceptStatus = z.union([RConceptStatus, RSuperconceptStatus])
export type AnyConceptStatus = ConceptStatus|SuperconceptStatus
export type AnyConceptStatusesByCid = _.Dictionary<AnyConceptStatus>

// I'm not sure if there is specific a need for this
// since currently subconcepts are structurally identical to concepts
export type SubconceptStatusesByCid = _.Dictionary<ConceptStatus>
export type SuperConceptStatusesByCid = _.Dictionary<SuperconceptStatus>

export type ConceptStatusesByCid = _.Dictionary<ConceptStatus>
